import React, { createContext, useContext, useState, useEffect } from 'react'
import { clearActivity, isSessionStale, recordActivity } from '../services/session'

// Before anything renders, drop a saved session the API would reject anyway:
// - saved before sign-in returned a JWT (user but no token)
// - the token expired or the user was inactive too long (e.g. the site was reopened later)
try {
  const hasToken = Boolean(localStorage.getItem('token'))
  if (localStorage.getItem('isAuthenticated') === 'true' && (!hasToken || isSessionStale())) {
    ;['user', 'token', 'isAuthenticated', 'lastActivity'].forEach((key) => localStorage.removeItem(key))
  }
} catch (_) {
  // Storage unavailable (private mode): nothing saved to clean up
}

// Create Auth Context
const AuthContext = createContext()

// Create Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(null)
  const [loading, setLoading] = useState(true)

  // Load the signed-in user and their JWT from localStorage on mount
  useEffect(() => {
    const loadAuth = () => {
      try {
        const savedUser = localStorage.getItem('user')
        const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true'
        const savedToken = localStorage.getItem('token')

        // A session needs both the user and the token the API issued at sign-in
        if (isAuthenticated && savedUser && savedToken) {
          setUser(JSON.parse(savedUser))
          setToken(savedToken)
        }
      } catch (error) {
        console.error('Error loading auth:', error)
        // Clear invalid data
        localStorage.removeItem('user')
        localStorage.removeItem('token')
        localStorage.removeItem('isAuthenticated')
      } finally {
        setLoading(false)
      }
    }

    loadAuth()
  }, [])

  // Login function: saves the user and the JWT the API returned at sign-in
  const login = (userData, authToken = null) => {
    setUser(userData)
    setToken(authToken)
    localStorage.setItem('user', JSON.stringify(userData))
    localStorage.setItem('isAuthenticated', 'true')
    recordActivity() // starts the inactivity clock (see SessionTimeout)
    // services/apiConfig.js sends this token with every API request
    if (authToken) {
      localStorage.setItem('token', authToken)
    } else {
      localStorage.removeItem('token')
    }
  }

  // Logout function
  const logout = () => {
    setUser(null)
    setToken(null)
    localStorage.removeItem('user')
    localStorage.removeItem('token')
    localStorage.removeItem('isAuthenticated')
    localStorage.removeItem('cart') // Clear cart on logout
    localStorage.removeItem('wishlist') // Clear wishlist on logout
    clearActivity()
  }

  // Update user function
  const updateUser = (userData) => {
    setUser(userData)
    localStorage.setItem('user', JSON.stringify(userData))
  }

  // Check if user is authenticated (based on user object, not token)
  const isAuthenticated = () => {
    return !!user
  }

  // Get auth headers for API requests (optional - only if using JWT)
  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json'
    }
    // Only add Authorization header if token exists (JWT systems)
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    return headers
  }

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated: isAuthenticated(),
    getAuthHeaders
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Custom hook to use Auth Context
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

